import { differenceInMonths, format, formatDistanceToNow, fromUnixTime } from 'date-fns';
import formatDistance from 'date-fns/formatDistance';
import it from 'date-fns/locale/it';

export const getDateDifference = (dateAt: string) => {
  if (!dateAt) return '';
  const timestamp = new Date(dateAt);
  const distance = formatDistance(Date.now(), timestamp, { addSuffix: false, locale: it });
  return distance;
};

export const getMonthDifference = (dateAt: string) => {
  const monthDiff = differenceInMonths(new Date(), new Date(dateAt));
  return monthDiff;
};

export const getTimeDifference = (dateAt: string) => {
  const timeDiff = formatDistanceToNow(fromUnixTime(Number(dateAt)), { locale: it });
  return timeDiff + ' fa';
};

export const weekDayMapObj = {
  0: 'Domenica',
  1: 'Lunedì',
  2: 'Martedì',
  3: 'Mercoledì',
  4: 'Giovedì',
  5: 'Venenerdì',
  6: 'Sabato',
};
export const formatTime = (time: string) => {
  // time format --> 1700
  let timeString = '';
  const splitTime: string[] | null = time.match(/.{1,2}/g); // --> splits string after 2 characters
  if (splitTime && splitTime.length) timeString = `${splitTime.join(':')}`;
  return timeString; // result --> 17:00
};

type TimeUnitType = 'minutes' | 'hours' | 'days' | 'months';

export function getDurationSinceDate(dateStr: string): { duration: number; unit: TimeUnitType } {
  const currentDate = new Date();
  const targetDate = new Date(!dateStr.includes('Z') ? dateStr + 'Z' : dateStr); // Add Z to ensure date is parsed as UTC  (ISO 8601 format)
  // Get time in UTC for both dates to avoid local timezone conversion issues
  const currentDateUTC = Date.UTC(
    currentDate.getUTCFullYear(),
    currentDate.getUTCMonth(),
    currentDate.getUTCDate(),
    currentDate.getUTCHours(),
    currentDate.getUTCMinutes(),
    currentDate.getUTCSeconds()
  );
  const targetDateUTC = Date.UTC(
    targetDate.getUTCFullYear(),
    targetDate.getUTCMonth(),
    targetDate.getUTCDate(),
    targetDate.getUTCHours(),
    targetDate.getUTCMinutes(),
    targetDate.getUTCSeconds()
  );
  const totalMilliseconds = currentDateUTC - targetDateUTC;
  const totalMinutes = Math.floor(totalMilliseconds / (1000 * 60));
  if (totalMinutes < 60) return { duration: totalMinutes, unit: 'minutes' };

  const hours = Math.floor(totalMinutes / 60);
  if (hours < 24) return { duration: hours, unit: 'hours' };

  const days = Math.floor(hours / 24);
  if (days < 30) return { duration: days, unit: 'days' };

  const months = Math.floor(days / 30);
  return { duration: months, unit: 'months' };
}

/**
 * Format a stored value for a native `type="date"` input, or return undefined when the
 * value isn't a date at all.
 *
 * `format()` THROWS `RangeError: Invalid time value` on an Invalid Date, which takes the
 * whole page down through the nearest error boundary. Several backend fields bound to a
 * date input are plain strings (`season_starts_at`, `season_ends_at`, `desired_start_date`
 * on a job position), so legacy records hold free text like "maggio" or "subito" — that
 * must render as an empty field, never as a crash.
 *
 * Lives here rather than next to the picker so the components that DISPLAY such a value
 * and the code that decides a stored value is unusable share one definition and cannot
 * drift apart.
 */
export const formatDateInputValue = (value: unknown): string | undefined => {
  if (!value) return undefined;

  const parsed = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(parsed.getTime())) return undefined;

  return format(parsed, 'yyyy-MM-dd');
};
